/**
 * UserFeedbackModal Component
 * 
 * Unified modal component that handles both positive and negative user feedback.
 * Renders different form sections based on feedback type with conditional validation.
 * 
 * Features:
 * - Conditional rendering based on feedback type (positive/negative)
 * - Dynamic labels and validation logic
 * - Shared components: title, checkbox, learn more link, action buttons
 * - Figma design compliant styling and spacing
 */

import React, { useState } from 'react';
import {
  Box,
  Text,
  Select,
  Textarea,
  Checkbox,
  Button,
  Group,
  Stack,
  Anchor,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import classes from './UserFeedbackModal.module.css';
import type { UserFeedbackModalProps, FeedbackFormData } from './types';

// =============================================================================
// CONSTANTS
// =============================================================================

const ISSUE_OPTIONS = [
  { value: 'incomplete', label: 'Incomplete Response' },
  { value: 'incorrect', label: 'Incorrect Response' },
  { value: 'error', label: 'Application Error' },
  { value: 'feature', label: 'Feature Not Working' },
  { value: 'timeout', label: 'Timed Out' },
] as const;

const USER_GUIDE_URL = 'https://fonterra.sharepoint.com/sites/GenerativeAIatFonterra/SitePages/Co-op-GPT-Training.aspx?csf=1&web=1&e=Vl4lZx&CID=35b76648-6822-4079-b745-b7b354fb01e4' as const;

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const UserFeedbackModal: React.FC<UserFeedbackModalProps> = ({
  type,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error = null,
  className,
  style,
}) => {
  // =============================================================================
  // STATE
  // =============================================================================
  
  const [formData, setFormData] = useState<FeedbackFormData>({
    issue: '',
    details: '',
    shareData: false,
  });

  // =============================================================================
  // DERIVED STATE
  // =============================================================================
  
  const isNegativeFeedback = type === 'negative';
  const isPositiveFeedback = type === 'positive';
  
  // Dynamic content based on feedback type
  const detailsLabel = isNegativeFeedback 
    ? "How can we improve your Co-op GPT experience?"
    : "Want to tell us more?";
    
  const cancelButtonText = 'Cancel'; // Always use "Cancel" for both feedback types
  
  // Validation logic
  const hasIssueSelected = Boolean(formData.issue?.trim());
  const hasDetails = Boolean(formData.details.trim());
  const hasRequiredFields = isNegativeFeedback ? (hasIssueSelected && hasDetails) : true;
  
  const canSubmit = !isSubmitting && hasRequiredFields;

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  const handleIssueChange = (value: string | null) => {
    setFormData(prev => ({ ...prev, issue: value || '' }));
  };

  const handleDetailsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target?.value;
    if (value === undefined) return;
    
    setFormData(prev => ({ ...prev, details: value }));
  };

  const handleShareDataChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target?.checked;
    if (checked === undefined) return;
    
    setFormData(prev => ({ ...prev, shareData: checked }));
  };

  const handleSubmit = async (): Promise<void> => {
    // Early return guards
    if (isSubmitting || !canSubmit) return;
    
    // Prepare clean submission data
    const submissionData: FeedbackFormData = {
      details: formData.details.trim(),
      shareData: formData.shareData,
      // Include issue for negative feedback (required field)
      ...(isNegativeFeedback && { issue: formData.issue!.trim() })
    };
    
    try {
      await onSubmit(submissionData);
    } catch (error) {
      // Error handling is delegated to parent component
      // Log for debugging purposes only
      if (process.env.NODE_ENV === 'development') {
        console.error('UserFeedbackModal: Submission error:', error);
      }
    }
  };

  const handleLearnMoreClick = (): void => {
    try {
      const newWindow = window.open(USER_GUIDE_URL, '_blank', 'noopener,noreferrer');
      
      // Fallback for popup blockers
      if (!newWindow) {
        // Create a temporary anchor element for navigation
        const anchor = document.createElement('a');
        anchor.href = USER_GUIDE_URL;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.click();
      }
    } catch (error) {
      // Fallback: navigate in the same window if all else fails
      if (process.env.NODE_ENV === 'development') {
        console.warn('UserFeedbackModal: Could not open external link:', error);
      }
      window.location.href = USER_GUIDE_URL;
    }
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================
  
  const renderIssueSection = () => {
    if (!isNegativeFeedback) return null;

    return (
      <Stack gap={9} className={classes.negativeSection}>
        <Text className={classes.label}>
          What went wrong? <span className={classes.requiredAsterisk}>*</span>
        </Text>
        <Select
          placeholder="Select from the following options"
          data={ISSUE_OPTIONS}
          value={formData.issue}
          onChange={handleIssueChange}
          rightSection={<IconChevronDown size={16} aria-hidden="true" style={{ color: 'var(--fonterra-icon-strong)' }} />}
          comboboxProps={{ withinPortal: false }}
          classNames={{
            input: classes.selectInput,
            dropdown: classes.selectDropdown,
            option: classes.selectOption,
          }}
          styles={{
            input: {
              '--input-color': 'var(--fonterra-input-text)',
              '--input-placeholder-color': 'var(--fonterra-input-placeholder)',
              color: 'var(--fonterra-input-text)',
              '&[data-placeholder]': {
                color: 'var(--fonterra-input-placeholder)',
                '--input-color': 'var(--fonterra-input-placeholder)',
                opacity: 1,
                '& *': {
                  color: 'var(--fonterra-input-placeholder)',
                  opacity: 1,
                },
              },
            },
          }}
        />
      </Stack>
    );
  };

  const renderDetailsSection = () => {
    const labelClass = isPositiveFeedback ? classes.optionalLabel : classes.label;
    
    return (
      <Stack gap={9} className={isPositiveFeedback ? classes.positiveSection : classes.negativeSection}>
        <Text className={labelClass}>
          {detailsLabel}{isPositiveFeedback && <span className={classes.optionalText}> (Optional)</span>}{isNegativeFeedback && <span className={classes.requiredAsterisk}> *</span>}
        </Text>
        <Textarea
          placeholder="Provide as much details as you can"
          value={formData.details}
          onChange={handleDetailsChange}
          rows={4}
          classNames={{ input: classes.textarea }}
        />
      </Stack>
    );
  };

  const renderSharedElements = () => {
    return (
      <>
        {/* Share data checkbox */}
        <Group gap={12} align="flex-start">
          <Checkbox
            checked={formData.shareData}
            onChange={handleShareDataChange}
            size="md"
            classNames={{
              root: classes.checkboxRoot,
              input: classes.checkboxInput,
            }}
          />
          <Text className={classes.checkboxLabel}>
            Share my prompt, response, and attachments with the Co-op GPT team for review
          </Text>
        </Group>

        {/* Learn more link */}
        <Anchor
          onClick={handleLearnMoreClick}
          className={classes.learnMoreLink}
        >
          Learn more about Generative AI at Fonterra
        </Anchor>

        {/* Error display */}
        {error && (
          <Text c="red" size="sm" className={classes.errorMessage}>
            {error}
          </Text>
        )}

        {/* Action buttons */}
        <Group justify="flex-end" gap={18}>
          <Button
            variant="subtle"
            onClick={onCancel}
            disabled={isSubmitting}
            className={classes.cancelButton}
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
            className={classes.submitButton}
            styles={{
              root: {
                '&:disabled, &[data-disabled]': {
                  backgroundColor: 'var(--fonterra-accent-positive-disabled)',
                  color: 'var(--fonterra-text-on-accent)',
                  opacity: 0.7,
                },
              },
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Group>
      </>
    );
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================
  
  return (
    <Box className={`${classes.container} ${className || ''}`} style={style}>
      <Stack gap={18}>
        {/* Header - Always shown */}
        <Text className={classes.header}>
          Thank you for your feedback!
        </Text>

        {/* Issue section - Only for negative feedback */}
        {renderIssueSection()}

        {/* Details section - Always shown with dynamic label */}
        {renderDetailsSection()}

        {/* Shared elements - Always shown */}
        {renderSharedElements()}
      </Stack>
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default UserFeedbackModal;
export type { UserFeedbackModalProps, FeedbackFormData };
